import { Box, Typography, Skeleton } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import '@fontsource/rubik';
import { getStageDisplayLabel } from '../../../utils/commonServices';

const STAGE_STYLE_MAP = {
  'Pre-Production':  { color: '#f0a040', softColor: '#fef0d6', Icon: DescriptionOutlinedIcon },
  'Production':      { color: '#00CEFF', softColor: '#d6f3ff', Icon: SettingsOutlinedIcon },
  'Post Production': { color: '#08231B', softColor: '#d9e6e0', Icon: MovieFilterOutlinedIcon },
  'Review':          { color: '#282831', softColor: '#e0e0e4', Icon: EditOutlinedIcon },
  'Complete':        { color: '#00D744', softColor: '#d6f7e0', Icon: CheckCircleOutlineOutlinedIcon },
};

const DEFAULT_STYLE = { color: '#6b7470', softColor: '#e8e8e6', Icon: SettingsOutlinedIcon };

function StatusCard({ config, count, isLoading }) {
  const { label, color, softColor, Icon } = config;

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '14px',
        pt: '22px',
        pb: '18px',
        px: '18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1.5,
        boxShadow: '0 1px 2px rgba(14,20,16,0.04)',
        overflow: 'hidden',
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* Top accent bar — 2px matching HTML .sc-top */}
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          right: 12,
          top: 0,
          height: 2,
          backgroundColor: color,
          borderRadius: '0 0 2px 2px',
        }}
      />

      {/* Icon */}
      {isLoading ? (
        <Skeleton variant="circular" width={52} height={52} sx={{ mt: 0.5 }} />
      ) : (
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: softColor,
            color: color,
            display: 'grid',
            placeItems: 'center',
            mt: '4px',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 26 }} />
        </Box>
      )}

      {/* Label */}
      {isLoading ? (
        <Skeleton width="70%" height={22} />
      ) : (
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: '17px',
            fontWeight: 700,
            color: color,
            letterSpacing: '-0.01em',
          }}
        >
          {label}
        </Typography>
      )}

      {/* Divider */}
      <Box
        sx={{
          width: '100%',
          borderTop: '1px dashed #E9EBE9',
          my: 0.25,
        }}
      />

      {/* Count */}
      {isLoading ? (
        <Skeleton width={60} height={50} />
      ) : (
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: '38px',
            fontWeight: 700,
            color: color,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            mt: '-2px',
          }}
        >
          {count ?? 0}
        </Typography>
      )}
    </Box>
  );
}

export default function ProjectStatusCards({ projectStatus, isLoading }) {
  const stages = (projectStatus?.stages || []).filter(s => s.stage !== 'Pre-Production');

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(8,35,27,0.04)',
      }}
    >
      {/* Header */}
      <Box sx={{ px: '20px', pt: '18px', pb: '4px' }}>
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: '15px',
            fontWeight: 700,
            color: '#08231B',
          }}
        >
          Current Project Status
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: '12.5px',
            color: '#6b7470',
            mt: 0.25,
          }}
        >
          Videos by stage
        </Typography>
      </Box>

      {/* Status legend */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          gap: '18px',
          pt: '4px',
          pb: '18px',
          px: 2,
          flexWrap: 'wrap',
        }}
      >
        {stages.map((s) => {
          const style = STAGE_STYLE_MAP[s.stage] || DEFAULT_STYLE;
          return (
            <Box key={s.stage_id} sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row' }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: style.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: '13px',
                  color: '#282831',
                }}
              >
                {getStageDisplayLabel(s.stage)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Cards grid */}
      <Box
        sx={{
          px: '20px',
          pb: '20px',
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: `repeat(${Math.min(stages.length || 4, 5)}, 1fr)`,
          },
          gap: '14px',
        }}
      >
        {isLoading
          ? [1, 2, 3, 4].map((k) => (
              <StatusCard key={k} config={{ ...DEFAULT_STYLE, label: '' }} count={0} isLoading />
            ))
          : stages.map((s) => {
              const style = STAGE_STYLE_MAP[s.stage] || DEFAULT_STYLE;
              return (
                <StatusCard
                  key={s.stage_id}
                  config={{ label: getStageDisplayLabel(s.stage), color: style.color, softColor: style.softColor, Icon: style.Icon }}
                  count={s.count}
                  isLoading={false}
                />
              );
            })}
      </Box>
    </Box>
  );
}
